// --- Dependencies -------------------------------------------------------------------------------
import { useRef } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useLang from "@/hooks/useLang";
import useOrganization from "@/hooks/useOrganization";
import usePopup from "@/hooks/usePopup";
import { readLangValue } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import SwitchOrganizationPopup from "@/UI/popups/SwitchOrganizationPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The active company badge, shared by the sidebar and the phone sheet so both surfaces show the same
 * thing the same way. One person may work in several companies, and knowing which one you are looking
 * at matters before you touch anything.
 */
export default function OrganizationSwitcher() {
    const lang = useLang((state) => state.lang) ?? "ar";
    const memberships = useAuth((state) => state.memberships);
    const organizationId = useOrganization((state) => state.organizationId);
    const addComp = usePopup((state) => state.addComp);
    const badgeRef = useRef<HTMLButtonElement>(null);

    const active = memberships.find((membership) => membership.organization._id === organizationId);
    const name = readLangValue(active?.organization.name, lang);
    const hasMore = memberships.length > 1;

    const handleSwitch = () => {
        const rect = badgeRef.current?.getBoundingClientRect();
        if (!rect) return;
        addComp({
            name: "switch-organization",
            Component: SwitchOrganizationPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
        });
    };

    return (
        <button ref={badgeRef} type="button" className="org-switcher" onClick={handleSwitch} disabled={!hasMore}>
            <Avatar name={name} picture={active?.organization.logo} size={28} bordered />
            <span className="org-switcher__name">{name}</span>
            {hasMore && (
                <span className="org-switcher__arrow">
                    <Icon name="chevron" size={16} />
                </span>
            )}
        </button>
    );
}
